/**
 * ProductService
 * Hier zit alle logica voor producten
 */

import type { Product, ProductDisplay, Grade, SustainabilityFactor, AppError } from '../models';
import { maakApplicatieFout } from '../models';
import { getProductById as dbGetProductById, getProductByBarcode as dbGetProductByBarcode, searchProducts as dbSearchProducts, getProductsByCategory, getRandomProducts } from '../database/queries';

/**
 * Cijfer vergelijkingswaarden voor sorteren en filteren
 * Hogere waarde = beter cijfer (A+ is 15, E- is 1).
 * Hiermee kunnen we makkelijk 'rekenen' met cijfers.
 */
const GRADE_VALUES: Record<Grade, number> = {
  'A+': 15, 'A': 14, 'A-': 13,
  'B+': 12, 'B': 11, 'B-': 10,
  'C+': 9, 'C': 8, 'C-': 7,
  'D+': 6, 'D': 5, 'D-': 4,
  'E+': 3, 'E': 2, 'E-': 1,
};

/**
 * String omzetten naar Grade type (want typescript zeurt anders).
 * Zorgt ervoor dat we zeker weten dat "A" een echte Grade is.
 */
function parseGrade(gradeString: string): Grade {
  const normalized = gradeString.trim() as Grade;
  if (normalized in GRADE_VALUES) {
    return normalized;
  }
  // Als het niet klopt maken we er gewoon E- van (lekker puh)
  return 'E-';
}

/**
 * Cijfers vergelijken
 * Wie is er beter?
 */
function compareGrades(grade1: Grade, grade2: Grade): number {
  return GRADE_VALUES[grade1] - GRADE_VALUES[grade2];
}

/**
 * Redenen omzetten naar mooie icoontjes.
 * Zodat de UI een mooi lijstje kan maken met 🧪 enzo.
 */
function mapSustainabilityFactors(product: Product): SustainabilityFactor[] {
  const factors: SustainabilityFactor[] = [];

  // 🧪 voor soep, 📦 voor doosje, 🐰 voor nijntje
  const reasonMappings = [
    { reason: product.reason1, reason_en: product.reason1_en, icon: '🧪', label: 'Ingrediënten' },
    { reason: product.reason2, reason_en: product.reason2_en, icon: '📦', label: 'Verpakking' },
    { reason: product.reason3, reason_en: product.reason3_en, icon: '🐰', label: 'Ethiek' },
    { reason: product.reason4, reason_en: product.reason4_en, icon: '🧪', label: 'Overig' },
  ];

  for (const mapping of reasonMappings) {
    if (mapping.reason && mapping.reason.trim().length > 0) {
      factors.push({
        icon: mapping.icon,
        label: mapping.label,
        description: mapping.reason,
        description_en: mapping.reason_en,
      });
    }
  }

  return factors;
}

/**
 * Database product ombouwen naar iets wat de app snapt (ProductDisplay).
 * Hier voegen we ook nep-data toe als de database leeg is (voor de demo).
 */
function transformToDisplay(product: Product): ProductDisplay {
  const display: ProductDisplay = {
    id: product.nummer,
    name: product.product,
    brand: product.merk,
    category: product.categorie,
    grade: parseGrade(product.milieuRating),
    overallReason: product.waaromRating,
    overallReason_en: product.waaromRating_en,
    sustainabilityFactors: mapSustainabilityFactors(product),
    barcode: product.barcode,
    // Plaatje gebruiken uit DB of verzinnen
    imageUrl: product.imageUrl || `https://placehold.co/400x400/e0f2f1/00695c?text=${encodeURIComponent(product.product.substring(0, 20))}`,
  };

  // Milieu dingen verzinnen op basis van het cijfer
  // In het echt komt dit uit een sensor ofzo
  // Maar die heb ik niet dus ik doe maar wat
  const gradeValue = GRADE_VALUES[display.grade];

  // Standaard waarden (als alles perfect is)
  let ph = 7.0;
  let tds = 300;
  let turb = 5;

  // Als de database het weet gebruiken we die, anders gokken we.
  // We baseren de gok op de 'grade'. Als de grade goed is (A),
  // dan zijn de waterwaardes waarschijnlijk ook goed (neutrale pH, lage troebelheid).
  if (product.ph !== undefined && product.tds !== undefined && product.turbidity !== undefined) {
    ph = product.ph;
    tds = product.tds;
    turb = product.turbidity;
  } else {
    // Gok algoritme
    if (gradeValue >= 13) { // A range
      ph = 7.0 + (Math.random() * 0.4 - 0.2); // 6.8 - 7.2 (Bijna perfect water)
      tds = 250 + Math.floor(Math.random() * 100); // 250 - 350
      turb = 2 + Math.floor(Math.random() * 5); // 2 - 7 (Glashelder)
    } else if (gradeValue >= 10) { // B range
      ph = 6.8 + (Math.random() * 0.8 - 0.4); // 6.4 - 7.2
      tds = 350 + Math.floor(Math.random() * 150); // 350 - 500
      turb = 10 + Math.floor(Math.random() * 10); // 10 - 20
    } else if (gradeValue >= 7) { // C range
      ph = 6.5 + (Math.random() * 1.5 - 0.5); // 6.0 - 7.5
      tds = 500 + Math.floor(Math.random() * 200); // 500 - 700
      turb = 25 + Math.floor(Math.random() * 20); // 25 - 45 (Beetje vies)
    } else if (gradeValue >= 4) { // D range
      ph = 5.5 + (Math.random() * 2.0); // 5.5 - 7.5 (wider variance)
      tds = 700 + Math.floor(Math.random() * 300); // 700 - 1000
      turb = 50 + Math.floor(Math.random() * 40); // 50 - 90
    } else { // E range
      // Acidic or Basic (Echt troep dit)
      ph = Math.random() > 0.5 ? 4.5 + Math.random() : 9.0 + Math.random();
      tds = 1000 + Math.floor(Math.random() * 1000); // 1000 - 2000
      turb = 100 + Math.floor(Math.random() * 200); // 100+ (Modderwater)
    }
  }

  display.phLevel = Number(ph.toFixed(1));
  display.tdsValue = tds;
  display.turbidity = turb;

  return display;
}

class ProductService {
   /**
    * Pak product via ID
    */
  async getProductById(id: number): Promise<ProductDisplay | null> {
    try {
      // Eerst de database bellen
      const product = await dbGetProductById(id);
      // Als we iets hebben, maken we het mooi, anders geven we niks terug
      return product ? transformToDisplay(product) : null;
    } catch (error) {
      console.error('ProductService: Fout bij ophalen product op ID:', error);

      // If it's already an AppError, re-throw it
      if ((error as AppError).type) {
        throw error;
      }

      throw maakApplicatieFout(
        'database_query',
        `Failed to retrieve product with ID ${id}`,
        true,
        () => this.getProductById(id),
        error instanceof Error ? error : undefined,
      );
    }
  }

   /**
    * Zoek op barcode
    */
  async getProductByBarcode(barcode: string): Promise<ProductDisplay | null> {
    try {
      const product = await dbGetProductByBarcode(barcode);

      if (!product) {
        throw maakApplicatieFout(
          'not_found',
          `Product with barcode ${barcode} not found`,
          false,
        );
      }

      return transformToDisplay(product);
    } catch (error) {
      // If it's a not_found error, re-throw it
      if ((error as AppError).type === 'not_found') {
        throw error;
      }

      // If it's already another AppError, re-throw it
      if ((error as AppError).type) {
        throw error;
      }

      console.error('ProductService: Fout bij ophalen product op barcode:', error);
      throw maakApplicatieFout(
        'database_query',
        `Kon product niet ophalen met barcode ${barcode}`,
        true,
        () => this.getProductByBarcode(barcode),
        error instanceof Error ? error : undefined,
      );
    }
  }

   /**
    * Zoeken op naam of merk
    */
  async searchProducts(query: string): Promise<ProductDisplay[]> {
    try {
      if (!query || query.trim().length === 0) {
        return [];
      }

      const products = await dbSearchProducts(query);
      return products.map(transformToDisplay);
    } catch (error) {
      console.error('ProductService: Fout bij zoeken producten:', error);

      // If it's already an AppError, re-throw it
      if ((error as AppError).type) {
        throw error;
      }

      throw maakApplicatieFout(
        'database_query',
        'Kon producten niet zoeken',
        true,
        () => this.searchProducts(query),
        error instanceof Error ? error : undefined,
      );
    }
  }

   /**
    * Sleutelwoordjes zoeken (regex is te moeilijk)
    */
  private getKeywords(text: string): string[] {
    if (!text) return [];
    const lower = text.toLowerCase();

    // Lijstje met belangrijke woorden
    const keywords = [
      'shampoo', 'conditioner', 'serum', 'oil', 'olie',
      'cream', 'creme', 'crème', 'lotion', 'gel',
      'soap', 'zeep', 'cleanser', 'reiniger',
      'mask', 'masker', 'deodorant', 'deo',
      'parfum', 'perfume', 'lipstick', 'mascara',
      'foundation', 'powder', 'poeder', 'blush',
      'bronzer', 'highlighter', 'eyeliner', 'eyeshadow', 'oogschaduw',
      'sunscreen', 'zonnebrand', 'scrub', 'toner', 'exfoliant'
    ];

    return keywords.filter(k => lower.includes(k));
  }

   /**
    * Betere producten zoeken voor de gebruiker
    * (Dus dezelfde categorie maar dan niet slecht voor de natuur)
    * We kijken heel streng naar de naam
    */
  async getAlternatives(productId: number): Promise<ProductDisplay[]> {
    try {
      // Eerst het originele ding ophalen, want we moeten weten wat we moeten vervangen
      const originalProduct = await dbGetProductById(productId);

      if (!originalProduct) {
        throw maakApplicatieFout(
          'not_found',
          `Product with ID ${productId} not found`,
          false,
        );
      }

      const originalGrade = parseGrade(originalProduct.milieuRating);

      // Alles uit dezelfde categorie trekken
      const categoryProducts = await getProductsByCategory(
        originalProduct.categorie,
      );

      // Determine strict keywords from original product name and category
      const nameKeywords = this.getKeywords(originalProduct.product);
      const categoryKeywords = this.getKeywords(originalProduct.categorie);

      // Unieke woorden verzamelen
      const strictKeywords = Array.from(new Set([...nameKeywords, ...categoryKeywords]));

      const hasStrictKeywords = strictKeywords.length > 0;

      // Filteren: moet beter cijfer hebben en niet zichzelf zijn.
      // We willen de gebruiker wel een ECHT alternatief geven,
      // dus we checken of de trefwoorden overeenkomen.
      const alternatives = categoryProducts
        .filter(p => {
          // 1. Niet zichzelf
          if (p.nummer === productId) return false;

          // 2. Moet beter of gelijk zijn (liefst beter)
          // "Beter" betekent hier > 
          const productGrade = parseGrade(p.milieuRating);
          if (compareGrades(productGrade, originalGrade) <= 0) return false;

          // 3. Streng controleren op trefwoorden
          if (hasStrictKeywords) {
            const candidateName = p.product.toLowerCase();
            const candidateCategory = p.categorie.toLowerCase();

            // Moet minstens EEN woord hebben dat overeenkomt
            // Anders krijg je zeep als je shampoo zoekt
            const matches = strictKeywords.some(k =>
              candidateName.includes(k) || candidateCategory.includes(k)
            );
            if (!matches) return false;
          }

          // Als we hier zijn, is het een goed alternatief!
          return true;
        })
        .map(transformToDisplay)
        .sort((a, b) => compareGrades(b.grade, a.grade)); // Sorteren op cijfer (hoog naar laag)

      return alternatives;
    } catch (error) {
      // If it's a not_found error, re-throw it
      if ((error as AppError).type === 'not_found') {
        throw error;
      }

      // If it's already another AppError, re-throw it
      if ((error as AppError).type) {
        throw error;
      }

      console.error('ProductService: Fout bij ophalen alternatieven:', error);
      throw maakApplicatieFout(
        'database_query',
        'Kon alternatieve producten niet ophalen',
        true,
        () => this.getAlternatives(productId),
        error instanceof Error ? error : undefined,
      );
    }
  }

   /**
    * Willekeurige producten voor op het beginscherm
    */
  async getDiscoveryProducts(limit: number = 5): Promise<ProductDisplay[]> {
    try {
      // Meer ophalen dan nodig om de kans op goede producten te vergroten
      // We halen 10x zoveel op en sorteren dan op cijfer
      const products = await getRandomProducts(limit * 10);
      const displayedProducts = products.map(transformToDisplay);
      
      // Sorteren op cijfer (A+ is 15, E- is 1) -> Hoog naar laag
      // Zo tonen we altijd de beste producten uit de selectie
      displayedProducts.sort((a: ProductDisplay, b: ProductDisplay) => compareGrades(b.grade, a.grade));
      
      return displayedProducts.slice(0, limit);
        
    } catch (error) {
      console.error('ProductService: Fout bij ophalen ontdek producten:', error);
      // Niet crashen svp
      return [];
    }
  }

   /**
   * Betere producten zoeken voor de gebruiker (browse mode)
   */
  async getAllProducts(): Promise<ProductDisplay[]> {
    try {
      // Hack: we gebruiken getRandomProducts met een hoge limiet omdat getAll nog niet bestaat
      // en we toch alles willen zien.
      const allRows = await getRandomProducts(1000); 
      return allRows.map(transformToDisplay);
    } catch (error) {
       console.error('Kon niet alle producten ophalen:', error);
       return [];
    }
  }

  /**
   * Twee producten ophalen om te vergelijken (voor de minigame).
   * We zorgen dat ze uit dezelfde categorie komen en een duidelijk verschil hebben.
   */
  async getComparisonPair(): Promise<ProductDisplay[]> {
    try {
      // 1. Pak een willekeurig product om mee te beginnen
      const randomStart = await getRandomProducts(1);
      if (randomStart.length === 0) throw new Error('Geen producten');
      
      const p1 = transformToDisplay(randomStart[0]);
      
      // 2. Haal alles op uit dezelfde categorie
      const categoryRows = await getProductsByCategory(p1.category);
      const candidates = categoryRows.map(transformToDisplay);

      // 3. Filter p1 eruit
      const others = candidates.filter(c => c.id !== p1.id);
      
      if (others.length === 0) {
        // Fallback: Als er geen andere in de categorie is, pak gewoon een random ander
        const fallback = await getRandomProducts(10);
        const fallbackP2 = fallback.map(transformToDisplay).find(p => p.id !== p1.id);
        return fallbackP2 ? [p1, fallbackP2] : [p1, p1]; // Should not happen with enough data
      }

      // 4. Kies een willekeurige tegenstander
      // Bonus: Probeer er een te vinden die een ANDERE grade heeft voor educatief effect
      const differentGrade = others.filter(c => c.grade !== p1.grade);
      
      let p2: ProductDisplay;
      if (differentGrade.length > 0) {
         p2 = differentGrade[Math.floor(Math.random() * differentGrade.length)];
      } else {
         p2 = others[Math.floor(Math.random() * others.length)];
      }

      // Even checken of p2 niet per ongeluk p1 is, of dat ze hetzelfde cijfer hebben.
      // We willen namelijk dat er een duidelijke winnaar is!
      let pogingen = 0;
      while ((p2.id === p1.id || p1.grade === p2.grade) && pogingen < 20) {
        p2 = others[Math.floor(Math.random() * others.length)]; // Blijf kiezen uit others
        pogingen++;
      }
      
      return [p1, p2];
    } catch (error) {
      console.error('ProductService: Fout bij ophalen battle paar:', error);
      return [];
    }
  }
}
// Singleton instantie exporteren
export const productService = new ProductService();

